/* eslint-disable react-hooks/exhaustive-deps */
import dayjs, { Dayjs } from "dayjs";
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from "react";
import { OpenPositionsData } from "../types/papertrade";
import { useFetchOpenPositions, useManualOrderOpen, useManualOrderUpdate, useManualOrderClosed, useManualOrderDelete, useFetchOpenPositionStocks } from "../services/papertrade.service";
import { formatNumber, formatNumberWithCommas } from "../helpers/common";
import { Space, Select, Slider, DatePicker, Dropdown, Button, Input, message, Drawer, Modal } from "antd";
import { DownCircleOutlined, LeftCircleOutlined, PlusCircleOutlined, ReloadOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { useFetchStocks, useFetchTradableStockOptions } from "../services/stock.service";
import { useFetchUserAccount } from "../services/useraccount.service";
import { useNavigate } from "react-router-dom";
import { useFetchSites } from "../services/sites.service";
import Images from "../assets/images";
import { storage } from "../utils/storage";
import { StockData, TradableStockOptions } from "../types/stock";
import { UserAccounts } from "../types/userAccount";
import { SiteData } from "../types/sites";
import { IResponse, IResponseModel } from "../types/response";

dayjs.extend(utc);

const OrderOpenPositions = () => {
    const { confirm } = Modal;
    const [data, setData] = useState<OpenPositionsData[]>([]);
    const totalInvestment = useRef<string>("0.00");
    const totalProfit = useRef<string>("0.00");
    const totalProfitPercentage = useRef<string>("0");
    const [loading, setLoading] = useState<boolean>(true);
    const defaultScoreRange: [number, number] = [0, 1000];
    const [aiScoreRange, setAIScoreRange] = useState<[number, number]>(defaultScoreRange);
    const [aiScoreMarks, setAIScoreMarks] = useState<any>({});
    const [dateRange, setDateRange] = useState(["", ""]);
    const [stocks, setStocksList] = useState<any[]>([]);
    const [openPositionstocks, setOpenPositionStocksList] = useState<any[]>([]);
    const [userAccount, setUserAccountList] = useState<any[]>([]);
    const [sites, setSitesList] = useState<any[]>([]);
    const [stockSymbols, setStockSymbols] = useState<string>("");
    const [siteId, setSiteId] = useState<any>(null);
    const [userAccountId, setUserAccountId] = useState<any>(null);
    const [orderUserAccountId, setOrderUserAccountId] = useState<any>(null);
    const [positionId, setpositionId] = useState<any>(null);
    const isAuthenticated = useRef(false);
    const isGuestUser = useRef(false);
    const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
    const [options, setOptions] = useState<{ label: string; value: string; lastPrice?: number }[]>([]);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [price, setPrice] = useState<number | string>("");
    const [open, setOpen] = useState(false);
    const [openManualCloseDrower, setManualCloseDrowerOpen] = useState(false);
    const [lot, setLot] = useState<number | null>(null);
    const [aiScore, setOpenAIScore] = useState('');
    const [orderFilledAt, setOrderFilledAt] = useState<Dayjs | null>(dayjs().utc());
    const [isEditMode, setIsEditMode] = useState(false);
    const [isOpenMode, setIsOpenMode] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const navigate = useNavigate();
    const { RangePicker } = DatePicker;
    const [stockSymbolOpen, setStockSymbolOpen] = useState(false);
    const token = useRef("");
    const isSyncInProgress = useRef(false);

    const getStocksList = useFetchStocks({
        onSuccess: (response: IResponseModel<StockData>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((stock: { stockSymbol: string; }) => ({
                    label: stock.stockSymbol,
                    value: stock.stockSymbol,
                }));
                setStocksList(options);
            } else {
                setStocksList([]);
            }
        },
    });

    const getOpenPositionStocksList = useFetchOpenPositionStocks({
        onSuccess: (response: IResponseModel<StockData>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((stock: { stockSymbol: string; }) => ({
                    label: stock.stockSymbol,
                    value: stock.stockSymbol,
                }));
                setOpenPositionStocksList(options);
            } else {
                setOpenPositionStocksList([]);
            }
        },
    });

    const getUserAccount = useFetchUserAccount({
        onSuccess: (response: IResponseModel<UserAccounts>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((account: { userAccountName: any; userAccountId: any; }) => ({
                    label: account.userAccountName,
                    value: account.userAccountId,
                }));
                const defaultUserAccountId = options.find((x: any) => x.label === "Manual Trade")?.value;
                handleUserAccountChange(defaultUserAccountId);
                if (defaultUserAccountId) {
                    handleUserAccountChange(defaultUserAccountId);
                }
                setUserAccountList(options);
            }
            else {
                setUserAccountList([]);
            }
        },
    });

    const fetchUserAccountList = () => {
        getUserAccount.mutate({});
    };

    const getSitesList = useFetchSites({
        onSuccess: (response: IResponseModel<SiteData>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((site: { siteName: any; siteId: any; }) => ({
                    label: site.siteName,
                    value: site.siteId,
                }));
                setSitesList(options);
            } else {
                setSitesList([]);
            }
        }
    });

    const getOpenPositions = useFetchOpenPositions({
        onSuccess: (response: IResponseModel<OpenPositionsData>) => {
            if (response.statusCode === 200 && response.data.length > 0) {
                const formattedData = response.data.map((item: any, index: { toString: () => any; }) => ({
                    ...item,
                    key: index.toString(),
                }));
                setData(formattedData);
                totalInvestment.current = formattedData.reduce((sum: any, item: { investment: any; }) => sum + (item.investment || 0), 0).toFixed(2);
                totalProfit.current = formattedData.reduce((sum: any, item: { unrealizedPnL: any; }) => sum + (item.unrealizedPnL || 0), 0).toFixed(2);
                totalProfitPercentage.current = ((parseFloat(totalProfit.current) / parseFloat(totalInvestment.current)) * 100).toFixed(2);
                setLoading(false);
            } else {
                setData([]);
                totalInvestment.current = "0.00";
                totalProfit.current = "0.00";
                totalProfitPercentage.current = "0.00";
                setLoading(false);
            }
            isSyncInProgress.current = false;
        },

    });

    const fetchOpenPosition = () => {
        setLoading(true);
        getOpenPositions.mutate({
            userAccountId: userAccountId ?? "",
            siteId: siteId ?? "",
            stockSymbols: stockSymbols,
            aiScoreRange: aiScoreRange.join('-'),
            positionSide: "",
            startDate: dateRange[0],
            endDate: dateRange[1],
            isGuestUser: isGuestUser.current
        });
    };

    const fetchStocksList = () => {
        getStocksList.mutate({});
    };

    const fetchOpenPositionStocksList = () => {
        getOpenPositionStocksList.mutate({});
    };
    const onRefresh = () => {
        fetchOpenPosition();
    };

    const handleDateChange = (_: any, dateStrings: any) => {
        const transformedDates = dateStrings.map((date: string, ix: number) => {
            if ((date ?? "") !== "") {
                if (ix === 0) {
                    return `${date}T00:00:00.000`;
                } else if (ix === 1) {
                    return `${date}T23:59:59.997`;
                }
            }
            return "";
        });
        setDateRange(transformedDates);
    };

    const handleSliderChange = (value: any) => {
        setAIScoreRange(value);
    };

    const handleUserAccountChange = (value: any) => {
        setUserAccountId(value);
    };

    const handleStockSymbolChange = (value: any) => {
        setStockSymbols(value?.join(','));
        if (value.length >= 1) {
            setStockSymbolOpen(false);
        }
    };

    const handleSitesChange = (value: any) => {
        setSiteId(value);
    };

    const fetchSitesList = () => {
        getSitesList.mutate({});
    };

    const syncAuthAndFetchData = () => {
        if (isSyncInProgress.current) {
            return;
        }
        isSyncInProgress.current = true;
        const [isAuth, isGuest] = ["isAuthenticated", "isGuestUser"].map(key =>
            JSON.parse(storage.getLocalProperty(key) ?? "false")
        );

        isAuthenticated.current = isAuth;
        isGuestUser.current = isGuest;
        token.current = (isGuest || isAuth) ? storage.getToken() : "";

        if (token.current !== "") {
            fetchStocksList();
            fetchOpenPositionStocksList();
            fetchSitesList();
            fetchUserAccountList();
            isSyncInProgress.current = false;
        } else {
            setTimeout(() => {
                isSyncInProgress.current = false;
                syncAuthAndFetchData();
            }, 100);
        }
    };

    useEffect(() => {
        if (token.current !== "" && !isSyncInProgress.current) {
            isSyncInProgress.current = true;
            fetchOpenPosition();
        } else {
            syncAuthAndFetchData();
        }
    }, [userAccountId, siteId, stockSymbols, dateRange, token.current]); // Runs only when dependencies change

    useEffect(() => {
        window.addEventListener("authStateChanged", syncAuthAndFetchData);
        return () => {
            window.removeEventListener("authStateChanged", syncAuthAndFetchData);
        };
    }, []); // Ensures event listener is added only once

    useEffect(() => {
        const [min, max] = aiScoreRange;
        const newMarks = {
            [min]: `${min}`,
            [max]: `${max}`,
        };
        setAIScoreMarks(newMarks);
    }, [aiScoreRange]);

    // Manual Order Drower Changes

    const validateField = (field: string, value: any) => {
        if (field === "selectedSingleSymbol") {
            if (!value) {
                return "Ticker is required.";
            }
        } else if (field === "selectedOption") {
            if (!value) {
                return "Contract is required.";
            }
        } else if (field === "lot") {
            if (!value) {
                return "Lot size is required.";
            }
        } else if (field === "price") {
            if (!value) {
                return "Entry Price is required.";
            }
        } else if (field === "orderFilledAt") {
            if (!value) {
                return "Entry DateTime is required.";
            }
        }
        return "";
    };

    const handleSymbolChange = (value: string | undefined) => {
        if (!value) {
            setSelectedSymbol(null);
            setOptions([]);
            return;
        }
        setSelectedSymbol(value);
        getTradableStockOptions.mutate(
            { stockSymbol: value },
            {
                onSuccess: (response: IResponseModel<TradableStockOptions>) => {
                    if (response?.data && Array.isArray(response.data)) {
                        const newOptions = response.data.map((item: { contractName: any; stockSymbol: any; lastPrice: any; }) => ({
                            label: item.contractName,
                            value: item.stockSymbol,
                            lastPrice: item.lastPrice,
                        }));
                        setOptions(newOptions);
                    } else {
                        console.warn("Unexpected response format:", response);
                    }
                },
                onError: (error: any) => {
                    console.error("Error fetching tradable stock options:", error);
                    setOptions([]); // Clear options in case of an error
                },
            }
        );
    };

    // const stockCreate = useStockCreate({
    //     onSuccess: (response: StockResponseData[]) => {
    //         let newOptions: any = [];
    //         response.forEach((x) => {
    //             newOptions.push({
    //                 label: x.contractName,
    //                 value: x.stockSymbol,
    //             });
    //         });
    //         setOptions(newOptions);
    //     },
    // });

    const manualOrderDelete = useManualOrderDelete({
        onSuccess: (response: IResponse) => {
            if (response.statusCode === 200) {
                onRefresh();
            }
        }
    });

    const deleteManualOrder = (selectedItem: any) => {
        manualOrderDelete.mutate({
            positionId: selectedItem.positionId,
        });
    };

    const showDeleteConfirm = (selectedItem: any) => {
        confirm({
            title: 'Are you sure you want to delete this trade?',
            icon: <ExclamationCircleFilled />,
            content: (<>
                Once you delete <b>{selectedItem.contractName}</b>, this action cannot be undone.
                Please confirm if you want to proceed.
            </>),
            okText: 'Confirm',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                deleteManualOrder(selectedItem);
            },
            onCancel() {
                console.log('Cancel');
            },
        });

    };

    const getTradableStockOptions = useFetchTradableStockOptions({
        onSuccess: (response: IResponseModel<TradableStockOptions>) => {
            if (response?.data && Array.isArray(response.data)) {
                const newOptions = response.data.map((item: { contractName: any; stockSymbol: any; lastPrice: any; }) => ({
                    label: item.contractName,
                    value: item.stockSymbol,
                    lastPrice: item.lastPrice,
                }));
                setOptions(newOptions);
            } else {
                console.warn("Unexpected response format:", response);
                setOptions([]);
            }
        },
        onError: (error: any) => {
            console.error("Error fetching tradable stock options:", error);
            setOptions([]);
        },
    });

    const handleOptionChange = (value: string | null) => {
        setSelectedOption(value);
        const selected = options.find((option) => option.value === value);
        if (selected && selected.lastPrice) {
            setPrice(selected.lastPrice.toFixed(2));
        }
    };

    const initialLotOptions = Array.from({ length: 20 }, (_, i) => ({
        value: i + 1,
        label: `${i + 1}`,
    }));

    const [lotOptions, setLotOptions] = useState(initialLotOptions);

    const handleLotChange = (value: any) => {
        if (value === null || value === undefined) {
            setLot(null);
            return;
        }
        const numericValue = Number(value);
        if (!isNaN(numericValue)) {
            if (numericValue > 0 && !lotOptions.find((option) => option.value === numericValue)) {
                setLotOptions((prevOptions) => [
                    ...prevOptions,
                    { value: numericValue, label: numericValue.toString() },
                ]);
            }
            setLot(numericValue);
        }
    };

    const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^0-9.]/g, '');
        if (/^\d*(\.\d{0,2})?$/.test(value)) {
            setPrice(value);
        }
    };

    const handlePriceBlur = () => {
        if (price) {
            const formattedPrice = formatNumberWithCommas(price);
            setPrice(formattedPrice);
        }
    };

    const handleOpenAIScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            setOpenAIScore(value);
        }
    };

    const showDrawer = () => {
        setOpen(true);
        setIsOpenMode(true);
        setIsEditMode(false);
    };

    const onCloseDrower = () => {
        setOpen(false);
        onClear();
        setIsEditMode(false);
        setIsOpenMode(false);
    };

    const closeManualOrderDrower = () => {
        setManualCloseDrowerOpen(false);
        onClear();
    };

    const onEditOrder = (selectedItem: any) => {
        if (!selectedItem) {
            console.warn("No item provided.");
            return;
        }
        setOrderUserAccountId(selectedItem.userAccountId);
        setpositionId(selectedItem.positionId);
        setSelectedSymbol(selectedItem.parentStockSymbol || null);
        setSelectedOption(selectedItem.contractName || null);
        setLot(selectedItem.lotSize || null);
        setPrice(selectedItem.entryPrice ? formatNumberWithCommas(selectedItem.entryPrice.toString()) : null);
        setOrderFilledAt(selectedItem.openedAt ? dayjs(selectedItem.openedAt) : null);
        setOpenAIScore(selectedItem.aiScore || null);
        setIsEditMode(true);
        setIsOpenMode(false);
        setOpen(true);
    };

    const onClosedOrder = (selectedItem: any) => {
        if (!selectedItem) {
            console.warn("No item provided.");
            return;
        }
        setOrderUserAccountId(selectedItem.userAccountId);
        setpositionId(selectedItem.positionId);
        setSelectedSymbol(selectedItem.parentStockSymbol || null);
        setSelectedOption(selectedItem.contractName || null);
        setLot(selectedItem.lotSize || null);
        setPrice(selectedItem.currentPrice ? formatNumberWithCommas(selectedItem.currentPrice.toString()) : null);
        setOrderFilledAt(dayjs().utc());
        setOpenAIScore(selectedItem.aiScore || null);
        setManualCloseDrowerOpen(true);
    };

    const handleSubmitManualOrder = () => {
        const selectedSymbolError = validateField('selectedSingleSymbol', selectedSymbol);
        if (selectedSymbolError) {
            message.error(selectedSymbolError);
            return;
        }

        const selectedOptionError = validateField('selectedOption', selectedOption);
        if (selectedOptionError) {
            message.error(selectedOptionError);
            return;
        }

        const lotError = validateField('lot', lot);
        if (lotError) {
            message.error(lotError);
            return;
        }

        const priceError = validateField('price', price);
        if (priceError) {
            message.error(priceError);
            return;
        }

        const orderFilledAtError = validateField('orderFilledAt', orderFilledAt);
        if (orderFilledAtError) {
            message.error(orderFilledAtError);
            return;
        }

        const formattedDate = orderFilledAt
            ? dayjs(orderFilledAt).format("YYYY-MM-DD HH:mm:ss")
            : null;

        const param = {
            "userAccountId": orderUserAccountId ?? null,
            "stockSymbol": selectedOption,
            "lotSize": lot,
            "entryPrice": price,
            "orderFilledAt": new Date(formattedDate?.toString() ?? ""),
            "aiScore": aiScore ?? null,
        };
        onSubmitManualOrder.mutate(param);
    };

    const onSubmitManualOrder = useManualOrderOpen({
        onSuccess: (response: IResponse) => {
            if (response.statusCode === 200) {
                onCloseDrower();
                onRefresh();
            }
        },
    });

    const handleSubmitManualOrderUpdate = () => {
        const selectedSymbolError = validateField('selectedSingleSymbol', selectedSymbol);
        if (selectedSymbolError) {
            message.error(selectedSymbolError);
            return;
        }

        const selectedOptionError = validateField('selectedOption', selectedOption);
        if (selectedOptionError) {
            message.error(selectedOptionError);
            return;
        }

        const lotError = validateField('lot', lot);
        if (lotError) {
            message.error(lotError);
            return;
        }

        const priceError = validateField('price', price);
        if (priceError) {
            message.error(priceError);
            return;
        }

        const orderFilledAtError = validateField('orderFilledAt', orderFilledAt);
        if (orderFilledAtError) {
            message.error(orderFilledAtError);
            return;
        }

        const formattedDate = orderFilledAt
            ? dayjs(orderFilledAt).format("YYYY-MM-DD HH:mm:ss")
            : null;

        const param = {
            "userAccountId": orderUserAccountId ?? null,
            "positionId": positionId ?? null,
            "lotSize": lot,
            "entryPrice": price,
            "orderFilledAt": new Date(formattedDate?.toString() ?? ""),
            "aiScore": aiScore ?? null,
        };
        onSubmitManualOrderUpdate.mutate(param);
    };

    const onSubmitManualOrderUpdate = useManualOrderUpdate({
        onSuccess: (response: IResponse) => {
            if (response.statusCode === 200) {
                onCloseDrower();
                onRefresh();
            }
        },
    });

    const handleSubmitManualOrderClosed = () => {
        const selectedSymbolError = validateField('selectedSingleSymbol', selectedSymbol);
        if (selectedSymbolError) {
            message.error(selectedSymbolError);
            return;
        }

        const selectedOptionError = validateField('selectedOption', selectedOption);
        if (selectedOptionError) {
            message.error(selectedOptionError);
            return;
        }

        const lotError = validateField('lot', lot);
        if (lotError) {
            message.error(lotError);
            return;
        }

        const priceError = validateField('price', price);
        if (priceError) {
            message.error(priceError);
            return;
        }

        const orderFilledAtError = validateField('orderFilledAt', orderFilledAt);
        if (orderFilledAtError) {
            message.error(orderFilledAtError);
            return;
        }

        const formattedDate = orderFilledAt
            ? dayjs(orderFilledAt).format("YYYY-MM-DD HH:mm:ss")
            : null;

        const param = {
            "userAccountId": orderUserAccountId ?? null,
            "positionId": positionId,
            "entryPrice": price,
            "orderFilledAt": new Date(formattedDate?.toString() ?? ""),
        };
        onSubmitManualOrderClosed.mutate(param);
    };

    const onSubmitManualOrderClosed = useManualOrderClosed({
        onSuccess: (response: IResponse) => {
            if (response.statusCode === 200) {
                closeManualOrderDrower();
                onRefresh();
                onClear();
            }
            else {
                console.warn("Unexpected Manual Order Closed.", response);
            }
        },
    });

    const onClear = () => {
        setSelectedSymbol(null);
        setSelectedOption(null);
        setOptions([]);
        setLot(null);
        setPrice('');
        setOrderFilledAt(dayjs().utc());
        setIsOpenMode(false);
        setIsEditMode(false);
        setOpenAIScore('');
    };

    const disableFutureAndToday = (current: any) => {
        if (isGuestUser.current) {
            return current && current >= dayjs().startOf('day');
        }
        return false;
    };

    return (
        <div className="relative h-full">
            <div className=' p-2.5 lg:p-6 rounded-xl shadow-box'>
                <div>
                    <h2 className='text-[17px] hidden 2xl:block lg:text-[21px] font-bold text-black text-start mb-2'>Open Positions</h2>
                </div>
                <div className="mb-2">
                    {/* Filter Button - Visible only on mobile */}
                    <div className="flex items-center gap-1 md:gap-3 ml-7 sm:ml-0">
                        <div className="w-full">
                            <button
                                onClick={() => setIsFilterOpen(!isFilterOpen)} // Toggle the filter section
                                className="py-[5px] px-2 lg:py-2 lg:px-4 text-black border text-sm sm:text-base border-blue-midnight rounded-md flex items-center justify-between gap-1.5 w-full"
                            >
                                Filter
                                {/* Conditional Icon Rendering */}
                                {isFilterOpen ? (
                                    <DownCircleOutlined className="text-blue-midnight" />
                                ) : (
                                    <LeftCircleOutlined className="text-blue-midnight" />
                                )}
                            </button>
                        </div>
                        <div className="flex gap-1 md:ap-3">
                            <button onClick={showDrawer} className={`py-2 px-2 lg:px-4 text-xs lg:text-base lg:py-2 text-white border ${isGuestUser.current ? 'bg-[#ccc] border-[#ccc]' : 'border-blue-midnight bg-blue-midnight hover:bg-transparent hover:text-blue-midnight'} rounded  flex items-center gap-1.5`} disabled={isGuestUser.current}>
                                <PlusCircleOutlined />
                                New
                            </button>
                            <div>
                                <button
                                    onClick={() => {
                                        onRefresh();
                                    }}
                                    className="py-2 px-2 lg:px-4 text-xs lg:text-base lg:py-2 text-white border border-blue-midnight bg-blue-midnight rounded hover:bg-transparent hover:text-blue-midnight flex items-center gap-1.5"
                                >
                                    <ReloadOutlined />
                                    {loading ? "Loading..." : "Refresh"}
                                </button>
                            </div>
                            <Drawer title="Manual Order" onClose={onCloseDrower} open={open}>
                                <div>
                                    <div>
                                        <p className="font-semibold">Ticker *</p>
                                        <div className="w-full flex items-center mt-2">
                                            <Select
                                                allowClear
                                                showSearch
                                                className="w-full 2xl:min-w-80 h-10 2xl:max-w-[500px] text-start multi-user-dropdown"
                                                placeholder="Select Stock Symbol"
                                                onChange={handleSymbolChange}
                                                value={selectedSymbol}
                                                options={stocks}
                                                disabled={!isOpenMode}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Contract *</p>
                                        <div className="w-full mt-2">
                                            <Select
                                                allowClear
                                                showSearch
                                                value={selectedOption}
                                                className="w-full 2xl:min-w-80 h-10 2xl:max-w-[500px] text-start multi-user-dropdown mb-2 md:mb-0"
                                                onChange={handleOptionChange}
                                                options={options}
                                                placeholder="Select Contract Name"
                                                disabled={!isOpenMode}
                                                filterOption={(input, option) => {
                                                    const label = option?.label?.toLowerCase().trim() ?? "";
                                                    const searchTerms = input.toLowerCase().trim().split(/\s+/);
                                                    return searchTerms.every((term) => label.includes(term));
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Lot *</p>
                                        <div className="w-full mt-2">
                                            <Select
                                                allowClear
                                                value={lot}
                                                className="w-full 2xl:min-w-80 h-10 2xl:max-w-[500px] text-start multi-user-dropdown mb-2 md:mb-0"
                                                onChange={handleLotChange}
                                                onSearch={(input) => handleLotChange(input)}
                                                options={lotOptions}
                                                placeholder="Select or Enter Lot"
                                                disabled={!isOpenMode && !isEditMode}
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "").toString().includes(input)
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Entry Price *</p>
                                        <div className="w-full mt-2">
                                            <Input
                                                prefix="$"
                                                value={price}
                                                defaultValue={price}
                                                onBlur={handlePriceBlur}
                                                onChange={handlePriceChange}
                                                className="h-10"
                                                placeholder="Enter Price (e.g., 100.00)"
                                                disabled={!isOpenMode && !isEditMode}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Entry DateTime *</p>
                                        <div className="w-full mt-2">
                                            <DatePicker
                                                className="w-full h-10"
                                                placeholder="Select Date"
                                                value={orderFilledAt}
                                                defaultValue={dayjs().utc()}
                                                showTime
                                                onChange={(dateString) => {
                                                    return setOrderFilledAt(dateString);
                                                }}
                                                format="DD-MMM-YYYY HH:mm"
                                                disabled={!isOpenMode && !isEditMode}
                                                disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Open AI Score</p>
                                        <div className="w-full mt-2">
                                            <Input
                                                value={aiScore}
                                                onChange={handleOpenAIScoreChange}
                                                className="h-10"
                                                placeholder="Open AI Score"
                                                disabled={!isOpenMode && !isEditMode}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end items-center mt-4">
                                    {isOpenMode && (
                                        <div className="w-full">
                                            <button onClick={handleSubmitManualOrder} disabled={!isOpenMode} className="p-2 lg:px-4 text-xs lg:text-base text-white border border-blue-midnight bg-blue-midnight rounded hover:bg-transparent hover:text-blue-midnight w-full">
                                                Open
                                            </button>
                                        </div>
                                    )}
                                    {isEditMode && (
                                        <div className="w-full">
                                            <button onClick={handleSubmitManualOrderUpdate} disabled={!isEditMode} className="p-2 px-2 lg:px-4 text-xs lg:text-base text-white border border-blue-midnight bg-blue-midnight rounded hover:bg-transparent hover:text-blue-midnight w-full">
                                                Update
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </Drawer>
                            <Drawer title="Manual Order Close" onClose={closeManualOrderDrower} open={openManualCloseDrower}>
                                <div>
                                    <div>
                                        <p className="font-semibold">Ticker *</p>
                                        <div className="w-full flex items-center mt-2">
                                            <Select
                                                allowClear
                                                showSearch
                                                className="w-full 2xl:min-w-80 h-10 2xl:max-w-[500px] text-start multi-user-dropdown"
                                                placeholder="Select Stock Symbol"
                                                onChange={handleSymbolChange}
                                                value={selectedSymbol}
                                                options={stocks}
                                                disabled={true}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Contract *</p>
                                        <div className="w-full mt-2">
                                            <Select
                                                allowClear
                                                showSearch
                                                value={selectedOption}
                                                className="w-full 2xl:min-w-80 h-10 2xl:max-w-[500px] text-start multi-user-dropdown mb-2 md:mb-0"
                                                onChange={(value) => setSelectedOption(value)}
                                                options={options}
                                                placeholder="Select Contract Name"
                                                disabled={true}
                                                filterOption={(input, option) => {
                                                    const label = option?.label?.toString().toLowerCase().trim() ?? ""; // Ensure label is a string and normalized
                                                    const searchTerms = input.toLowerCase().trim().split(/\s+/); // Split input into multiple terms
                                                    return searchTerms.every((term) => label.includes(term)); // Ensure all terms match some part of the label
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Lot *</p>
                                        <div className="w-full mt-2">
                                            <Select
                                                allowClear
                                                value={lot}
                                                className="w-full 2xl:min-w-80 h-10 2xl:max-w-[500px] text-start multi-user-dropdown mb-2 md:mb-0"
                                                onChange={handleLotChange}
                                                onSearch={(input) => handleLotChange(input)}
                                                options={lotOptions}
                                                placeholder="Select or Enter Lot"
                                                disabled={!isOpenMode && !isEditMode}
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "").toString().includes(input)
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Exit Price *</p>
                                        <div className="w-full mt-2">
                                            <Input
                                                prefix="$"
                                                value={price}
                                                onBlur={handlePriceBlur}
                                                onChange={handlePriceChange}
                                                className="h-10"
                                                placeholder="Enter Price (e.g., 100.00)"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Exit DateTime *</p>
                                        <div className="w-full mt-2">
                                            <DatePicker
                                                className="w-full h-10"
                                                placeholder="Select Date"
                                                value={orderFilledAt}
                                                showTime
                                                onChange={(dateString) => {
                                                    return setOrderFilledAt(dateString);
                                                }}
                                                format="DD-MMM-YYYY HH:mm"
                                                disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end items-center mt-4">
                                    <div className="w-full">
                                        <button onClick={handleSubmitManualOrderClosed} className="p-2 px-2 lg:px-4 text-xs lg:text-base text-white border border-blue-midnight bg-blue-midnight rounded hover:bg-transparent hover:text-blue-midnight w-full">
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </Drawer>
                        </div>
                    </div>
                    {isFilterOpen && (
                        <div className="rounded-lg shadow-md mt-2 lg:mt-4 p-2 sm:p-3.5">
                            <div className="block lg:flex items-center justify-end">
                                <div className='w-full'>
                                    <div className="block md:flex justify-between items-center gap-3 w-full">
                                        <div className="w-full">
                                            <Select
                                                value={userAccountId}
                                                className="w-full h-10 2xl:max-w-[600px] text-start multi-user-dropdown mb-2 md:mb-0"
                                                onChange={handleUserAccountChange}
                                                allowClear
                                                showSearch
                                                options={userAccount}
                                                placeholder="Select User Account"
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>
                                        <div className="w-full">
                                            <Space direction="vertical" size={12} className='w-full mb-2 md:mb-0'>
                                                <RangePicker
                                                    onChange={handleDateChange}
                                                    className='p-2 w-full datepicker-table'
                                                    disabledDate={disableFutureAndToday}
                                                />
                                            </Space>
                                        </div>
                                        <div className="w-full">
                                            <Select
                                                allowClear
                                                showSearch
                                                value={siteId}
                                                className='w-full h-10 2xl:max-w-[600px] text-start multi-user-dropdown mb-2 md:mb-0'
                                                onChange={handleSitesChange}
                                                options={sites}
                                                placeholder="Select Site"
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>
                                        <div className="w-full">
                                            <Select
                                                mode="multiple"
                                                allowClear
                                                className="w-full 2xl:max-w-[600px] text-start multi-user-dropdown"
                                                placeholder="Select Stock Symbol"
                                                onChange={handleStockSymbolChange}
                                                options={openPositionstocks}
                                                open={stockSymbolOpen} // Control dropdown visibility
                                                onDropdownVisibleChange={(visible) => setStockSymbolOpen(visible)} // Update open state on dropdown visibility change
                                            />
                                        </div>
                                        <div className="flex justify-between gap-4 items-center mt-2 sm:mt-0">
                                            <div className='max-md:w-[95%]'>
                                                <p className='absolute text-sm font-semibold '>Open AI Score</p>
                                                <Slider range
                                                    value={aiScoreRange}
                                                    marks={aiScoreMarks}
                                                    min={0} max={1000}
                                                    onChange={(evt) => { handleSliderChange(evt) }}
                                                    className='w-full md:w-32 lg:w-60 top-4 range-slider h-5' />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="overflow-auto">
                    {loading ? (
                        <div className="space-y-4">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className="animate-pulse flex w-full">
                                    <div className="w-1/12 h-6 bg-gray-300 rounded hidden lg:block"></div>
                                    <div className="w-2/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-2/12 h-6 bg-gray-300 rounded mx-1"></div>
                                </div>
                            ))}
                        </div>
                    ) :
                        data.length === 0 ? (
                            <div>
                                <p>No records found</p>
                            </div>
                        ) : (
                            <div className={`overflow-x-auto ${isFilterOpen ? 'h-[calc(100vh-450px)] md:h-[calc(100vh-255px)] xl:h-[calc(100vh-270px)]' : 'h-[calc(100vh-160px)] xl:h-[calc(100vh-170px)]'
                                }`}
                            >
                                <table className="min-w-full w-full">
                                    <thead className='sticky top-0 z-10'>
                                        <tr className='bg-[#000639] text-white text-xs lg:text-sm'>
                                            <th className="hidden lg:table-cell py-3 md:py-4 px-3.5 text-left border border-white whitespace-nowrap" rowSpan={2}>No.</th>
                                            <th className="py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap" rowSpan={2}>Trade Info</th>
                                            <th className="py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap" rowSpan={2}>Lot</th>
                                            <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap' rowSpan={2}>P&L</th>
                                            <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap' colSpan={2}>Option Price</th>
                                            <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap' colSpan={2}>Spot Price</th>
                                            <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap' rowSpan={2}>Target</th>
                                            <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap' rowSpan={2}>SL</th>
                                            <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap' colSpan={2}>Open Interest</th>
                                            <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap' rowSpan={2}>Time</th>
                                            <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap' rowSpan={2}>AI Score</th>
                                            <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap' rowSpan={2}>Invested</th>
                                            <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap' rowSpan={2}></th>
                                        </tr>
                                        <tr className='bg-[#000639] text-white text-xs lg:text-sm'>
                                            {/* Option Price */}
                                            <th className="py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap">Entry</th>
                                            <th className="py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap">Current</th>
                                            {/* Spot Price */}
                                            <th className="py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap">Entry</th>
                                            <th className="py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap">Current</th>
                                            {/* Open Interest */}
                                            <th className="py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap">Entry</th>
                                            <th className="py-3 md:py-4 px-2.5 lg:px-3.5 text-center border border-white whitespace-nowrap">Current</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((item, index) => (
                                            <tr
                                                key={index}
                                                className={`border-b border-black ${item.unrealizedPnLPercentage != null
                                                    ? item.unrealizedPnLPercentage >= 100
                                                        ? 'bg-green-dark-green-500 text-white'
                                                        : item.unrealizedPnLPercentage >= 50
                                                            ? 'bg-green-light-green-200'
                                                            : item.unrealizedPnLPercentage <= -50
                                                                ? 'bg-red-200'
                                                                : ''
                                                    : ''
                                                    }`}>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 text-left hidden lg:block'>{index + 1}</td>
                                                {/* Trade Info */}
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-start">
                                                    <a
                                                        href={`https://tradingview.com/chart?symbol=${item.parentStockSymbol}&interval=1D`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className='text-blue-500 hover:underline'

                                                    >
                                                        {item.parentStockSymbol}
                                                    </a>{' '}
                                                    <a
                                                        href={`https://finance.yahoo.com/quote/${item.yahooFinanceStockSymbol}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className='text-blue-500 hover:underline'

                                                    >
                                                        {item.contractName.replace(item.parentStockSymbol, '').trim()}
                                                    </a>
                                                </td>
                                                {/* Lot */}
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span>{item.lotSize}</span>
                                                </td>
                                                {/* P&L */}
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>
                                                    <span
                                                        className={`${item.unrealizedPnL == null
                                                            ? 'text-black'
                                                            : item.unrealizedPnLPercentage >= 100
                                                                ? 'text-white'
                                                                : item.unrealizedPnL < 0
                                                                    ? 'text-red-700'
                                                                    : 'text-green-700'
                                                            }`}
                                                    >
                                                        ${item.unrealizedPnL == null ? '0.00' : formatNumberWithCommas(item.unrealizedPnL)} ({item.unrealizedPnL == null ? '0' : item.unrealizedPnLPercentage.toFixed(2)}%)                                                    </span>
                                                </td>
                                                {/* Option Price*/}
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span> ${formatNumberWithCommas(item.entryPrice)}</span>
                                                </td>
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span> ${formatNumberWithCommas(item.currentPrice)}
                                                        {/* <span className={`${item.currentPricePercentage == null
                                                        ? 'text-black'
                                                        : item.currentPricePercentage < 0
                                                            ? 'text-red-700'
                                                            : 'text-green-700'
                                                        }`}>({item.entryPrice == null ? '0' : item.currentPricePercentage.toFixed(2)}%)</span> */}
                                                    </span>
                                                </td>
                                                {/* Spot Price */}
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span> ${formatNumberWithCommas(item.underlyingPrice)}</span>
                                                </td>
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span> ${formatNumberWithCommas(item.spotCurrentPrice)} <span className={`${item.spotCurrentPricePercentage == null
                                                        ? 'text-black'
                                                        : item.spotCurrentPricePercentage < 0
                                                            ? 'text-red-700'
                                                            : 'text-green-700'
                                                        }`}>({item.underlyingPrice == null ? '0' : item.spotCurrentPricePercentage.toFixed(2)}%)</span></span>
                                                </td>
                                                {/* SL and Target */}
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span> ${formatNumberWithCommas(item.targetPrice)}</span>
                                                </td>
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span> ${formatNumberWithCommas(item.stopLossPrice)}</span>
                                                </td>
                                                {/* Open Interest */}
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span> {formatNumber(item.openInterest)}</span>
                                                </td>
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span> {formatNumber(item.currentOpenInterest)} <span className={`${item.currentOpenInterestPercentage == null
                                                        ? 'text-black'
                                                        : item.currentOpenInterestPercentage < 0
                                                            ? 'text-red-700'
                                                            : 'text-green-700'
                                                        }`}>({item.openInterest == null ? '0' : item.currentOpenInterestPercentage.toFixed(2)}%)</span></span>
                                                </td>

                                                {/* Time */}
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span>{item.openedAtAgo}</span>
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-700 text-white text-xs rounded px-2 py-1 z-10">
                                                        {new Date(item.openedAt).toLocaleString('en-GB', {
                                                            weekday: 'short',
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit',
                                                            hour12: true,
                                                        })}
                                                    </div>
                                                </td>
                                                {/* AI Score */}
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>
                                                    <span
                                                    >
                                                        {Math.round(item.aiScore)}
                                                    </span>
                                                </td>
                                                {/* Investment */}
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>${formatNumberWithCommas(item.investment)}</td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>
                                                    <Dropdown
                                                        menu={{
                                                            items: [
                                                                {
                                                                    key: '1',
                                                                    label: (
                                                                        <p onClick={() => onEditOrder(item)}>Edit</p>
                                                                    ),
                                                                },
                                                                {
                                                                    key: '2',
                                                                    label: (
                                                                        <p onClick={() => onClosedOrder(item)}>Close</p>
                                                                    ),
                                                                },
                                                                {
                                                                    key: '3',
                                                                    label: (
                                                                        <p onClick={() => showDeleteConfirm(item)} style={{ color: 'red' }} >Delete</p>
                                                                    ),
                                                                },
                                                            ],
                                                        }}
                                                        placement="bottomRight"
                                                        arrow={{ pointAtCenter: true }}
                                                    >
                                                        <Button className={`p-2.5 h-0 w-9 ${!isGuestUser.current ? 'hover:!border-blue-midnight' : ''}`} disabled={isGuestUser.current}>
                                                            <img src={Images.threedots} alt="Three-dots" />
                                                        </Button>
                                                    </Dropdown>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                </div>
            </div>
            <div className="absolute bottom-0 left-0 bg-white flex justify-around items-center text-xs md:text-sm lg:text-sm w-full py-2 px-2 font-bold rounded-t-lg shadow-[0px_-2px_10px_0px_rgba(0,0,0,0.1)] border-t border-gray-200">
                <p>No. of Positions: {data.length}</p>
                <p>Total Investment: ${formatNumberWithCommas(totalInvestment.current)}</p>
                <p>Profit: ${formatNumberWithCommas(totalProfit.current)} ({totalProfitPercentage.current}%)</p>
            </div>
        </div>
    );
};

export default OrderOpenPositions;
